import { Button, ButtonGroup, pxToRem } from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import { Input } from 'antd';
import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Modal } from 'src/shared/components/Modal';
import { color, font } from 'src/shared/styles/variables';

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        maxWidth: pxToRem(500),
        minHeight: pxToRem(300),
        margin: '0 auto',
        borderRadius: pxToRem(20),

        '& .ant-modal-body': {
            padding: pxToRem(28, 24, 30),
        },
    },
});

const ModalHeader = styled('h3')(
    {
        color: color.ebonyClay,
        textAlign: 'left',
        marginBottom: pxToRem(8),
    },
    font.heavy(font.size.largeExtended),
);

const SubHeader = styled.span({
    color: color.blueBayoux,
    ...font.book(font.size.medium),
});

const StyledButtonGroup = styled(ButtonGroup)({
    marginTop: pxToRem(24),
});

const TextArea = styled(Input.TextArea)({
    '&&': {
        maxWidth: 670,
        marginTop: pxToRem(24),
        padding: 11,
        color: color.pickledBluewood,
        border: `2px solid ${color.linkWaterLight}`,
        ...font.medium(font.size.base),
    },
});

interface Props {
    visible: boolean;
    onSubmit: (answers: {
        isUseful: boolean | null;
        feedbackText?: string;
        featureRecordName: string;
    }) => void;
    onBack: () => void;
}

export const DemographicFeedbackModal: React.FC<Props> = ({ visible, onBack, onSubmit }) => {
    const { t } = useTranslation();
    const [feedbackText, setFeedbackText] = useState('');
    const [isUseful, setIsUsefull] = useState<boolean | null>(null);

    return (
        <React.Fragment>
            <StyledModal
                visible={visible}
                footer={null}
                width="auto"
                maskClosable={false}
                closable={false}
            >
                <ModalHeader>{t('providers:demographic_feedback_modal.header')}</ModalHeader>
                <SubHeader>{t('providers:demographic_feedback_modal.sub_header')}</SubHeader>
                <StyledButtonGroup>
                    <Button
                        onClick={() => {
                            setIsUsefull(false);
                        }}
                        variant={isUseful === false ? 'primary' : 'secondary'}
                    >
                        {t('providers:demographic_feedback_modal.answer.no')}
                    </Button>
                    <Button
                        onClick={() => {
                            setIsUsefull(true);
                        }}
                        variant={isUseful === true ? 'primary' : 'secondary'}
                    >
                        {t('providers:demographic_feedback_modal.answer.yes')}
                    </Button>
                </StyledButtonGroup>

                <TextArea
                    rows={10}
                    placeholder={t('providers:demographic_feedback_modal.answer.other')}
                    value={feedbackText}
                    onChange={({ target }) => {
                        setFeedbackText(target.value);
                    }}
                />

                <StyledButtonGroup>
                    <Button onClick={onBack} variant="secondary">
                        {t('providers:demographic_feedback_modal.button.back')}
                    </Button>
                    <Button
                        onClick={() => {
                            onSubmit({
                                isUseful,
                                feedbackText,
                                featureRecordName: 'demographics_race_filter',
                            });
                            setIsUsefull(null);
                            setFeedbackText('');
                        }}
                        disabled={isUseful === null}
                        variant="primary"
                    >
                        {t('providers:demographic_feedback_modal.button.submit')}
                    </Button>
                </StyledButtonGroup>
            </StyledModal>
        </React.Fragment>
    );
};
